/** Display rules for business identity, metrics, and memory.
 * These helpers never write to the API and never treat seeded illustrations as confirmed performance.
 */

export const SEED_PROFILE = {
  business_name: "Atelier Nima",
  industry: "Handmade home goods",
  description: "A growing maker business focused on considered objects for everyday spaces.",
};

export const SEED_METRICS = {
  revenue: 12480000,
  orders: 38,
  customers: 27,
  conversion: 3.8,
};

const MEMORY_ORDER = ["verified", "observation", "hypothetical", "ai", "demo", "unconfirmed"];

const HYPOTHETICAL_PATTERNS = [
  /hypothetical/i,
  /what if/i,
  /imagine if/i,
  /for example/i,
  /\be\.g\./i,
  /سناریوی فرضی/,
  /فرض کن/,
  /فرض کنید/,
  /اگر فرض/,
  /مثال فرضی/,
  /این فقط یک مثال/,
  /این یک تست/,
  /داده آزمایشی/,
  /داده نمونه/,
  /مثلاً/,
  /مثلا/,
];

function sameNumber(left, right) {
  const a = Number(left);
  const b = Number(right);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
}

export function isSeedProfile(business) {
  if (!business) return false;
  return business.business_name === SEED_PROFILE.business_name
    && business.industry === SEED_PROFILE.industry
    && business.description === SEED_PROFILE.description;
}

export function isSeedMetricSet(metrics) {
  if (!metrics || typeof metrics !== "object") return false;
  return sameNumber(metrics.revenue, SEED_METRICS.revenue)
    && sameNumber(metrics.orders, SEED_METRICS.orders)
    && sameNumber(metrics.customers, SEED_METRICS.customers)
    && sameNumber(metrics.conversion, SEED_METRICS.conversion);
}

export function profileKind(business) {
  if (!business || !String(business.business_name || "").trim()) return "missing";
  if (business.sample_data || isSeedProfile(business)) return "demonstration";
  return "customer";
}

export function metricsAreVerified(business) {
  const metrics = business?.metrics;
  if (!metrics || typeof metrics !== "object") return false;
  if (business.sample_data || isSeedMetricSet(metrics)) return false;
  const source = String(metrics.source || metrics.verification || "").toLowerCase();
  return metrics.verified === true || source === "customer" || source === "verified" || source === "confirmed";
}

export function formatVerifiedMetric(key, metrics, lang) {
  const value = metrics?.[key];
  if (value === null || value === undefined || value === "") return null;
  const locale = lang === "fa" ? "fa-IR" : "en-GB";
  if (key === "conversion") return `${Number(value).toLocaleString(locale)}%`;
  if (key === "revenue") {
    const amount = Number(value).toLocaleString(locale);
    const currency = String(metrics.currency || "").trim();
    return currency ? `${amount} ${currency}` : amount;
  }
  return Number(value).toLocaleString(locale);
}

function mentionsSeedCluster(content) {
  const text = String(content || "");
  const hasRevenue = /12[,.]?480[,.]?000|12\.5|۱۲[,٫.]?۵|12480000/.test(text);
  const hasOrders = text.includes("38") || text.includes("۳۸");
  const hasCustomers = text.includes("27") || text.includes("۲۷");
  const hasConversion = /3\.8|۳[,٫.]۸/.test(text);
  return [hasRevenue, hasOrders, hasCustomers, hasConversion].filter(Boolean).length >= 3;
}

export function classifyMemory(item) {
  const verification = String(item?.verification || "").toLowerCase();
  const source = String(item?.source || "").toLowerCase();
  const kind = String(item?.kind || "").toLowerCase();
  const content = String(item?.content || "");
  if (["demo", "sample", "seed", "test"].includes(source) || kind === "test" || kind === "demo" || mentionsSeedCluster(content)) return "demo";
  if (["ai", "assistant", "noorin", "suggestion", "openai"].includes(source) || kind === "suggestion") return "ai";
  if (verification === "confirmed" || verification === "verified") return "verified";
  if (HYPOTHETICAL_PATTERNS.some((pattern) => pattern.test(content))) return "hypothetical";
  if (verification === "reported" || source === "intake" || kind === "observation") return "observation";
  return "unconfirmed";
}

export function groupMemory(items) {
  const groups = Object.fromEntries(MEMORY_ORDER.map((key) => [key, []]));
  (items || []).forEach((item) => {
    groups[classifyMemory(item)].push(item);
  });
  return MEMORY_ORDER.filter((key) => groups[key].length).map((key) => ({ kind: key, items: groups[key] }));
}

export function homepagePriceLeaks(value) {
  const text = typeof value === "string" ? value : JSON.stringify(value);
  const patterns = [
    /9\s*million/i,
    /29\s*million/i,
    /49\s*million/i,
    /million toman/i,
    /میلیون تومان/,
    /۹\s*میلیون/,
    /۲۹\s*میلیون/,
    /۴۹\s*میلیون/,
    /\b9,?000,?000\b/,
    /\b29,?000,?000\b/,
    /\b49,?000,?000\b/,
  ];
  return patterns.filter((pattern) => pattern.test(text)).map((pattern) => String(pattern));
}
